// /app/frontend/src/components/admin/SampleRequestsTab.jsx
// Admin tab for managing fabric sample requests

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Package, Search, Filter, Truck, CheckCircle, Clock, XCircle,
  Mail, MapPin, Phone, User, Eye, RefreshCw, Loader2, CheckSquare, Square
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui/select';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-800', icon: Package },
  shipped: { label: 'Shipped', color: 'bg-purple-100 text-purple-800', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle }
};

const SampleRequestsTab = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [shipModalOpen, setShipModalOpen] = useState(false);
  const [shipData, setShipData] = useState({ tracking_number: '', carrier: 'USPS' });
  const [submitting, setSubmitting] = useState(false);
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkShipModalOpen, setBulkShipModalOpen] = useState(false);
  const [bulkStatusModalOpen, setBulkStatusModalOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');

  const token = localStorage.getItem('token');

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter === 'all' 
        ? `${API_URL}/api/admin/fabric-collections/sample-requests`
        : `${API_URL}/api/admin/fabric-collections/sample-requests?status=${statusFilter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch sample requests');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const filteredRequests = requests.filter(req => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      req.customer_name?.toLowerCase().includes(search) ||
      req.customer_email?.toLowerCase().includes(search) ||
      req.id?.toLowerCase().includes(search) ||
      req.tracking_number?.toLowerCase().includes(search)
    );
  });

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/fabric-collections/sample-requests/${requestId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status updated to ${newStatus}`);
      fetchRequests();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleShipRequest = async () => {
    if (!shipData.tracking_number) {
      toast.error('Please enter a tracking number');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/admin/fabric-collections/sample-requests/${selectedRequest.id}/ship`,
        shipData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.email_notification === 'sent') {
        toast.success('Samples marked as shipped! Email notification sent to customer.');
      } else {
        toast.success('Samples marked as shipped!');
      }
      
      setShipModalOpen(false);
      setShipData({ tracking_number: '', carrier: 'USPS' });
      fetchRequests();
    } catch (error) {
      toast.error('Failed to ship samples');
    } finally {
      setSubmitting(false);
    }
  };

  // Bulk selection helpers
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredRequests.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredRequests.map(r => r.id));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const isSelected = (id) => selectedIds.includes(id);
  const allSelected = filteredRequests.length > 0 && selectedIds.length === filteredRequests.length;

  // Bulk ship multiple requests
  const handleBulkShip = async () => {
    if (!shipData.tracking_number) {
      toast.error('Please enter a tracking number');
      return;
    }

    if (selectedIds.length === 0) {
      toast.error('No requests selected');
      return;
    }

    setSubmitting(true);
    let successCount = 0;
    let failCount = 0;

    for (const requestId of selectedIds) {
      try {
        await axios.put(
          `${API_URL}/api/admin/fabric-collections/sample-requests/${requestId}/ship`,
          shipData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        successCount++;
      } catch (error) {
        failCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} request(s) shipped successfully! Emails sent to customers.`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} request(s) failed to ship`);
    }

    setBulkShipModalOpen(false);
    setShipData({ tracking_number: '', carrier: 'USPS' });
    setSelectedIds([]);
    fetchRequests();
    setSubmitting(false);
  };

  // Bulk status change
  const handleBulkStatusChange = async () => {
    if (!bulkStatus) {
      toast.error('Please select a status');
      return;
    }

    if (selectedIds.length === 0) {
      toast.error('No requests selected');
      return;
    }

    setSubmitting(true);
    let successCount = 0;
    let failCount = 0;

    for (const requestId of selectedIds) {
      try {
        await axios.put(
          `${API_URL}/api/admin/fabric-collections/sample-requests/${requestId}/status?status=${bulkStatus}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        successCount++;
      } catch (error) {
        failCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} request(s) updated to ${bulkStatus}`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} request(s) failed to update`);
    }

    setBulkStatusModalOpen(false);
    setBulkStatus('');
    setSelectedIds([]);
    fetchRequests();
    setSubmitting(false);
  };

  const openDetailModal = (request) => {
    setSelectedRequest(request);
    setDetailModalOpen(true);
  };

  const openShipModal = (request) => {
    setSelectedRequest(request);
    setShipModalOpen(true);
  };

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  // Stats
  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    processing: requests.filter(r => r.status === 'processing').length,
    shipped: requests.filter(r => r.status === 'shipped').length,
    delivered: requests.filter(r => r.status === 'delivered').length
  };

  return (
    <div className="space-y-6" data-testid="sample-requests-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">Sample Requests</h2>
          <p className="text-sm text-neutral-500">Manage fabric sample requests from customers</p>
        </div>
        <Button onClick={fetchRequests} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <p className="text-2xl font-bold text-neutral-900">{stats.total}</p>
          <p className="text-sm text-neutral-500">Total Requests</p>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
          <p className="text-sm text-yellow-600">Pending</p>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <p className="text-2xl font-bold text-blue-700">{stats.processing}</p>
          <p className="text-sm text-blue-600">Processing</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <p className="text-2xl font-bold text-purple-700">{stats.shipped}</p>
          <p className="text-sm text-purple-600">Shipped</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <p className="text-2xl font-bold text-green-700">{stats.delivered}</p>
          <p className="text-sm text-green-600">Delivered</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              placeholder="Search by name, email, or tracking..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="shipped">Shipped</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
          <span className="text-sm font-medium text-amber-800">
            {selectedIds.length} request{selectedIds.length > 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setBulkStatusModalOpen(true)}
              className="text-neutral-700"
            >
              Change Status
            </Button>
            <Button
              size="sm"
              onClick={() => setBulkShipModalOpen(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Truck className="w-4 h-4 mr-1" />
              Ship Selected
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedIds([])}
              className="text-neutral-500"
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Requests Table */}
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 mx-auto text-neutral-300 mb-4" />
            <p className="text-neutral-500">No sample requests found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={toggleSelectAll}
                      className="text-neutral-400 hover:text-neutral-600"
                    >
                      {allSelected ? (
                        <CheckSquare className="w-5 h-5 text-amber-600" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Samples</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Tracking</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {filteredRequests.map((request) => (
                  <tr key={request.id} className={`hover:bg-neutral-50 ${isSelected(request.id) ? 'bg-amber-50' : ''}`}>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleSelectOne(request.id)}
                        className="text-neutral-400 hover:text-neutral-600"
                      >
                        {isSelected(request.id) ? (
                          <CheckSquare className="w-5 h-5 text-amber-600" />
                        ) : (
                          <Square className="w-5 h-5" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-neutral-900">{request.customer_name}</p>
                        <p className="text-sm text-neutral-500">{request.customer_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-neutral-600">
                        {request.colors?.length || request.materials?.length || 0} color(s)
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-4 py-3">
                      {request.tracking_number ? (
                        <div className="text-sm">
                          <p className="font-mono text-neutral-900">{request.tracking_number}</p>
                          <p className="text-xs text-neutral-500">{request.carrier || 'USPS'}</p>
                        </div>
                      ) : (
                        <span className="text-sm text-neutral-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-500">
                      {request.created_at ? format(new Date(request.created_at), 'MMM d, yyyy') : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openDetailModal(request)}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {request.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStatusChange(request.id, 'processing')}
                          >
                            Process
                          </Button>
                        )}
                        {(request.status === 'pending' || request.status === 'processing') && (
                          <Button
                            size="sm"
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                            onClick={() => openShipModal(request)}
                          >
                            <Truck className="w-4 h-4 mr-1" />
                            Ship
                          </Button>
                        )}
                        {request.status === 'shipped' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 border-green-200 hover:bg-green-50"
                            onClick={() => handleStatusChange(request.id, 'delivered')}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Delivered
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Sample Request Details
            </DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4 py-4">
              {/* Customer Info */}
              <div className="bg-neutral-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4 text-neutral-400" />
                  <span className="font-medium">{selectedRequest.customer_name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-neutral-400" />
                  <span>{selectedRequest.customer_email}</span>
                </div>
                {selectedRequest.customer_phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-neutral-400" />
                    <span>{selectedRequest.customer_phone}</span>
                  </div>
                )}
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-neutral-400 mt-0.5" />
                  <span>
                    {selectedRequest.shipping_address}<br />
                    {selectedRequest.city}, {selectedRequest.state} {selectedRequest.zip_code}
                  </span>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-500">Status:</span>
                {getStatusBadge(selectedRequest.status)}
              </div>

              {/* Tracking Info */}
              {selectedRequest.tracking_number && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-purple-800">Tracking Information</p>
                  <p className="font-mono text-lg text-purple-900">{selectedRequest.tracking_number}</p>
                  <p className="text-sm text-purple-600">{selectedRequest.carrier}</p>
                  {selectedRequest.shipped_at && (
                    <p className="text-xs text-purple-500 mt-1">
                      Shipped: {format(new Date(selectedRequest.shipped_at), 'MMM d, yyyy h:mm a')}
                    </p>
                  )}
                </div>
              )}

              {/* Colors Requested */}
              <div>
                <p className="text-sm font-medium text-neutral-700 mb-2">Colors Requested:</p>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {(selectedRequest.colors || []).map((color, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm bg-neutral-50 rounded px-3 py-2">
                      <span className="text-neutral-500">{color.brand_name}</span>
                      <span className="text-neutral-300">→</span>
                      <span className="text-neutral-500">{color.collection_name}</span>
                      <span className="text-neutral-300">→</span>
                      <span className="text-neutral-500">{color.material_name}</span>
                      <span className="text-neutral-300">→</span>
                      <span className="font-medium text-neutral-900">{color.name}</span>
                    </div>
                  ))}
                  {selectedRequest.colors?.length === 0 && selectedRequest.materials?.length > 0 && (
                    <p className="text-sm text-neutral-500">Legacy material-based request</p>
                  )}
                </div>
              </div>

              {/* Notes */}
              {selectedRequest.notes && (
                <div>
                  <p className="text-sm font-medium text-neutral-700 mb-1">Customer Notes:</p>
                  <p className="text-sm text-neutral-600 bg-neutral-50 rounded p-3">{selectedRequest.notes}</p>
                </div>
              )}

              {/* Dates */}
              <div className="text-xs text-neutral-400 pt-2 border-t">
                <p>Created: {selectedRequest.created_at ? format(new Date(selectedRequest.created_at), 'MMM d, yyyy h:mm a') : '—'}</p>
                {selectedRequest.updated_at && (
                  <p>Updated: {format(new Date(selectedRequest.updated_at), 'MMM d, yyyy h:mm a')}</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ship Modal */}
      <Dialog open={shipModalOpen} onOpenChange={setShipModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Ship Samples
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-neutral-600">
              Enter tracking information. An email notification will be sent to the customer.
            </p>
            
            <div>
              <Label>Tracking Number *</Label>
              <Input
                value={shipData.tracking_number}
                onChange={(e) => setShipData(prev => ({ ...prev, tracking_number: e.target.value }))}
                placeholder="e.g., 9400111899223456789012"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Carrier</Label>
              <Select 
                value={shipData.carrier} 
                onValueChange={(val) => setShipData(prev => ({ ...prev, carrier: val }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USPS">USPS</SelectItem>
                  <SelectItem value="UPS">UPS</SelectItem>
                  <SelectItem value="FedEx">FedEx</SelectItem>
                  <SelectItem value="DHL">DHL</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selectedRequest && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Customer:</strong> {selectedRequest.customer_name}
                </p>
                <p className="text-sm text-amber-700">
                  <strong>Email:</strong> {selectedRequest.customer_email}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShipModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleShipRequest}
              disabled={submitting || !shipData.tracking_number}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Truck className="w-4 h-4 mr-2" />
              )}
              Ship & Notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Ship Modal */}
      <Dialog open={bulkShipModalOpen} onOpenChange={setBulkShipModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Bulk Ship Samples
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                You are about to ship <strong>{selectedIds.length}</strong> sample request{selectedIds.length > 1 ? 's' : ''} with the same tracking information.
              </p>
              <p className="text-xs text-amber-600 mt-1">
                Email notifications will be sent to all customers.
              </p>
            </div>
            
            <div>
              <Label>Tracking Number *</Label>
              <Input
                value={shipData.tracking_number}
                onChange={(e) => setShipData(prev => ({ ...prev, tracking_number: e.target.value }))}
                placeholder="e.g., 9400111899223456789012"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Carrier</Label>
              <Select 
                value={shipData.carrier} 
                onValueChange={(val) => setShipData(prev => ({ ...prev, carrier: val }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USPS">USPS</SelectItem>
                  <SelectItem value="UPS">UPS</SelectItem>
                  <SelectItem value="FedEx">FedEx</SelectItem>
                  <SelectItem value="DHL">DHL</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkShipModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleBulkShip}
              disabled={submitting || !shipData.tracking_number}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Truck className="w-4 h-4 mr-2" />
              )}
              Ship {selectedIds.length} Request{selectedIds.length > 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Status Change Modal */}
      <Dialog open={bulkStatusModalOpen} onOpenChange={setBulkStatusModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Bulk Status Change
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-neutral-600">
              Change status for <strong>{selectedIds.length}</strong> selected request{selectedIds.length > 1 ? 's' : ''}.
            </p>
            
            <div>
              <Label>New Status</Label>
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkStatusModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleBulkStatusChange}
              disabled={submitting || !bulkStatus}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SampleRequestsTab;
